import { type CatalogPermissionKey, type PermissionKey } from '@aysar/contracts';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/shared/components/ui/Badge/Badge';
import { Checkbox } from '@/shared/components/ui/Checkbox/Checkbox';
import { Label } from '@/shared/components/ui/Label/Label';
import { cn } from '@/shared/utils/cn/cn.util';

import {
  usePermissionGroups,
  type PermissionGroup,
} from '../../hooks/usePermissionGroups/usePermissionGroups';

export interface PermissionChecklistProps {
  value: readonly PermissionKey[];
  onChange: (value: PermissionKey[]) => void;
  isDisabled?: boolean;
  /**
   * Names the field's description and error, threaded from the `custom` field's
   * `control.describedById`. On the group, not on a row: the rows are dynamic
   * and repeating one description across all of them is worse than naming the
   * group that holds them.
   */
  describedById?: string;
}

/**
 * What a role may do, grouped by resource.
 *
 * The grouping comes from `usePermissionGroups` — one translated projection of the
 * code-declared catalog, shared with the per-user grant/deny editor — so a new
 * `integrations.<capability>.manage` (N21) appears here with no edit to this file.
 *
 * A `.manage` key is marked as the wildcard it is. It is NOT auto-selected on
 * behalf of its siblings and it does not disable them — the api resolves the
 * wildcard at check time, and a UI that pre-ticked the narrow keys would store a
 * grant set that no longer matches what the operator chose the moment the
 * catalog grows a new action.
 *
 * **Keys the operator cannot hand out are disabled** (N28): the api refuses a role
 * carrying authority its author does not hold, and only a super admin may hand out
 * the global wildcard. Offering those checkboxes would mean an operator ticks
 * boxes, writes copy, presses Save and *then* learns they were never allowed —
 * with no way to tell which box caused it. The api stays the authority; this is the
 * same predicate from `libs/contracts`, never a second one (N22).
 *
 * Select-all is **per group and grantable-only** (P10): ticking a resource heading
 * takes the keys the operator may actually hand out and leaves the refused ones
 * alone, so the shortcut can never build a role the save will reject.
 */
export function PermissionChecklist({
  value,
  onChange,
  isDisabled = false,
  describedById,
}: PermissionChecklistProps) {
  const { t } = useTranslation('admins');
  const groups = usePermissionGroups();
  const groupId = useId();
  const selected = new Set<string>(value);

  const toggle = (key: CatalogPermissionKey) => {
    const next = new Set(selected);
    if (!next.delete(key)) {
      next.add(key);
    }
    onChange([...next] as PermissionKey[]);
  };

  const setGroup = (group: PermissionGroup, isOn: boolean) => {
    const next = new Set(selected);
    for (const key of group.grantableKeys) {
      if (isOn) {
        next.add(key);
      } else {
        next.delete(key);
      }
    }
    onChange([...next] as PermissionKey[]);
  };

  return (
    <div
      role="group"
      aria-labelledby={groupId}
      aria-describedby={describedById}
      className="flex flex-col gap-lg"
    >
      <p id={groupId} className="text-caption text-muted-foreground">
        {t('roles.form.permissionsHint')}
      </p>

      {groups.map((group) => {
        const chosenInGroup = group.grantableKeys.filter((key) => selected.has(key)).length;
        const headerState =
          chosenInGroup === 0
            ? false
            : chosenInGroup === group.grantableKeys.length
              ? true
              : ('indeterminate' as const);
        const selectAllId = `${groupId}-${group.id}-all`;

        return (
          <fieldset key={group.id} className="flex flex-col gap-sm">
            {/* The heading and the shortcut are ONE element: rendering the group
                name twice (a `sr-only` legend plus a visible label) reads as two
                headings to a screen reader and makes every query ambiguous. */}
            <legend className="flex items-center gap-sm">
              {/* Nothing grantable in this group means no shortcut — a checkbox
                  that cannot change anything is worse than its absence. */}
              {group.grantableKeys.length === 0 || isDisabled ? null : (
                <Checkbox
                  id={selectAllId}
                  checked={headerState}
                  aria-label={t('roles.form.selectAllIn', { resource: group.label })}
                  onCheckedChange={() => {
                    setGroup(group, headerState !== true);
                  }}
                />
              )}
              <Label
                htmlFor={group.grantableKeys.length === 0 ? undefined : selectAllId}
                className="text-label font-medium text-foreground"
              >
                {group.label}
              </Label>
            </legend>

            {/* `auto-rows-fr` + `h-full` on the cards: ten resources carry exactly one
                key, and without equal rows a one-line card sat beside a three-line one
                and the grid read as ragged. */}
            <div className="grid auto-rows-fr grid-cols-1 gap-sm sm:grid-cols-2">
              {group.items.map((item) => {
                const checkboxId = `${groupId}-${item.key}`;
                const labelId = `${checkboxId}-label`;
                const rowDescriptionId = `${checkboxId}-description`;
                const isLocked = isDisabled || item.isRefusedToGrant;

                return (
                  <div
                    key={item.key}
                    className={cn(
                      'flex h-full items-start gap-sm rounded-md border border-border p-md transition-colors duration-fast ease-standard',
                      // The global wildcard is the single most consequential row
                      // in the list; it should not look like the others.
                      item.isGlobalManage && 'border-destructive/40 bg-destructive/5',
                      isLocked ? 'opacity-60' : 'hover:border-primary/40',
                    )}
                  >
                    <Checkbox
                      id={checkboxId}
                      checked={selected.has(item.key)}
                      disabled={isLocked}
                      // Named by the permission alone, described by the rest. A Radix
                      // checkbox is a `button`, so its accessible name comes from its
                      // own content — pointing at the label's first line is what stops
                      // a screen reader announcing the description and the raw key as
                      // part of the control's name.
                      aria-labelledby={labelId}
                      aria-describedby={rowDescriptionId}
                      onCheckedChange={() => {
                        toggle(item.key);
                      }}
                    />
                    {/* The whole card is the label, so the hit target is the card
                        rather than a 16px box beside it. */}
                    <Label htmlFor={checkboxId} className="flex min-w-0 flex-1 flex-col gap-xs">
                      <span
                        id={labelId}
                        className="flex flex-wrap items-center gap-sm text-foreground"
                      >
                        {item.label}
                        {item.isWildcard ? (
                          <Badge tone={item.isGlobalManage ? 'danger' : 'warning'}>
                            {t('roles.form.wildcard')}
                          </Badge>
                        ) : null}
                      </span>
                      <span
                        id={rowDescriptionId}
                        className="text-caption font-normal text-muted-foreground"
                      >
                        {item.description}
                      </span>
                      {/* The raw key, out of the label line: inline, it broke every
                          Arabic label onto a second row. Monospace and quiet, because
                          it is what an operator quotes in a support thread. */}
                      <span className="font-mono text-tiny text-muted-foreground/70">
                        {item.key}
                      </span>
                      {item.isRefusedToGrant ? (
                        <span className="text-tiny text-warning">
                          {t('roles.form.notYoursToGrant')}
                        </span>
                      ) : null}
                    </Label>
                  </div>
                );
              })}
            </div>
          </fieldset>
        );
      })}
    </div>
  );
}
